export type UserRole = 'rep' | 'manager' | 'admin';

export type DealStage = 'new' | 'contacted' | 'demo_done' | 'test_drive' | 'closing' | 'sold';
export type DealStatus = 'active' | 'slowing' | 'no_activity';
export type DealPriority = 'hot' | 'warm' | 'cold';

export type DemoType = 'walkaround' | 'test_drive' | 'quote_review' | 'followup_video' | 'phone_call' | 'appointment_set';
export type NextStep = 'call' | 'follow_up' | 'appointment' | 'send_numbers' | 'trade_review' | 'close' | 'sold' | 'delivery';
export type ActivityType = DemoType | 'text_sent' | 'email_sent' | 'note_added';

export type FeedbackColor = 'green' | 'yellow' | 'red';

export interface Dealership {
  id: string;
  name: string;
  logo_url?: string;
  primary_color?: string;
  created_at: string;
}

export interface User {
  id: string;
  dealership_id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string;
  created_at: string;
}

export interface Deal {
  id: string;
  dealership_id: string;
  rep_id: string;
  rep_name: string;
  customer_name: string;
  phone: string;
  email: string;
  vehicle: string;
  stage: DealStage;
  estimated_value: number;
  priority: DealPriority;
  status: DealStatus;
  next_step: NextStep | null;
  notes: string;
  created_at: string;
  last_activity_at: string;
  license_image_path?: string | null;
  license_data?: Record<string, unknown> | null;
}

export interface Demo {
  id: string;
  dealership_id: string;
  deal_id: string;
  rep_id: string;
  demo_type: DemoType;
  notes: string;
  next_step: NextStep;
  created_at: string;
}

export interface Activity {
  id: string;
  dealership_id: string;
  deal_id: string;
  rep_id: string;
  rep_name: string;
  activity_type: ActivityType;
  details: string;
  customer_name?: string;
  created_at: string;
}

export interface CustomerFeedback {
  id: string;
  dealership_id: string;
  deal_id: string;
  rep_id: string;
  rating_color: FeedbackColor;
  notes: string;
  created_at: string;
}

export interface LeaderboardScore {
  id: string;
  dealership_id: string;
  rep_id: string;
  rep_name: string;
  period_type: 'today' | 'week' | 'month';
  score: number;
  demos_count: number;
  followup_rate: number;
  deals_moved: number;
  sold_count: number;
  csi_score: number;
  updated_at: string;
}

export interface FloorRep {
  id: string;
  name: string;
  active_deals: number;
  demos: number;
  last_activity: string;
  hot_deals: number;
  status: DealStatus;
}

export const DEMO_TYPE_LABELS: Record<DemoType, string> = {
  walkaround: 'Walkaround',
  test_drive: 'Test Drive',
  quote_review: 'Quote Review',
  followup_video: 'Follow-Up Video',
  phone_call: 'Phone Call',
  appointment_set: 'Appointment Set',
};

export const NEXT_STEP_LABELS: Record<NextStep, string> = {
  call: 'Call',
  follow_up: 'Follow-up',
  appointment: 'Appointment',
  send_numbers: 'Send Numbers',
  trade_review: 'Trade Review',
  close: 'Close',
  sold: 'Sold',
  delivery: 'Delivery',
};

export const STAGE_LABELS: Record<DealStage, string> = {
  new: 'New',
  contacted: 'Contacted',
  demo_done: 'Demo Done',
  test_drive: 'Test Drive',
  closing: 'Closing',
  sold: 'Sold',
};

export const STAGE_ORDER: DealStage[] = ['new', 'contacted', 'demo_done', 'test_drive', 'closing', 'sold'];

export type WishlistUrgency = 'hot' | 'warm' | 'cold';
export type WishlistStatus = 'active' | 'fulfilled' | 'dropped';
export type WishlistSourcingStatus =
  | 'searching'
  | 'incoming'
  | 'located'
  | 'notified'
  | 'appointment_set';

export interface WishlistItem {
  id: string;
  dealership_id: string;
  rep_id: string;
  rep_name?: string;
  customer_name: string;
  customer_phone: string | null;
  customer_email: string | null;
  deal_id: string | null;
  make: string;
  model: string | null;
  year_min: number | null;
  year_max: number | null;
  max_price: number | null;
  color: string | null;
  notes: string | null;
  urgency: WishlistUrgency;
  status: WishlistStatus;
  sourcing_status: WishlistSourcingStatus;
  created_at: string;
  updated_at: string;
}

export const URGENCY_LABELS: Record<WishlistUrgency, string> = {
  hot: 'Hot',
  warm: 'Warm',
  cold: 'Cold',
};

export const SOURCING_STATUS_LABELS: Record<WishlistSourcingStatus, string> = {
  searching: 'Searching',
  incoming: 'Incoming',
  located: 'Located',
  notified: 'Notified',
  appointment_set: 'Appt Set',
};

export type InventoryStatus = 'available' | 'incoming' | 'sold';

export interface InventoryUnit {
  id: string;
  dealership_id: string;
  vin: string | null;
  stock_number: string | null;
  make: string;
  model: string | null;
  year: number | null;
  color: string | null;
  price: number | null;
  status: InventoryStatus;
  eta_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}
